import type React from 'react'
import { IconChevronRight } from './icons/IconChevronRight'
import { IconMinusCircle } from './icons/IconMinusCircle'
import { IconWarningTriangle } from './icons/IconWarningTriangle'
import styles from './StatusBanner.module.css'

export type StatusBannerVariant = 'closed' | 'restricted' | 'advisory'

export interface StatusBannerProps {
  variant: StatusBannerVariant
  /** Banner heading (defaults to Figma canonical copy per variant). */
  title?: string
  /** Banner subheading (defaults to Figma canonical copy per variant). */
  subtitle?: string
  /** Fires on tap — analytics + routing to disruption details. */
  onTap?: () => void
}

const DEFAULT_COPY: Record<StatusBannerVariant, { title: string; subtitle: string }> = {
  closed: { title: 'Station closed', subtitle: 'All day' },
  restricted: { title: 'Limited service', subtitle: 'Step-free access only' },
  advisory: { title: 'Service advisory', subtitle: 'Tap for more info' },
}

function variantClass(variant: StatusBannerVariant): string {
  if (variant === 'closed') return styles.closed
  if (variant === 'restricted') return styles.restricted
  return styles.advisory
}

/**
 * Status banner atom (Figma `StatusBanner` component set, `6454:17830`).
 * Used by `StationCardView` as the overlay in the Closed / Restricted /
 * Advisory sandwich variants.
 *
 * Three variants:
 * - `closed` — danger fill (statusDanger*); station cannot be routed from/to.
 * - `restricted` — warning fill (statusWarning*); limited service.
 * - `advisory` — soft fill; non-blocking disruption (mirrors Suggester advisory).
 *
 * Variant fills + foreground are CSS-driven. Icon + chevron inherit `color`
 * via `currentColor`. The banner is a `<button>` for native keyboard + focus
 * semantics; tapping emits `statusBannerTapped` via `onTap`.
 *
 * Animation: the parent `StationCardView` mounts/unmounts the banner
 * via a CSS `transition` container (opacity + max-height) so appearance
 * and disappearance animate in place without remounting the card body.
 */
export function StatusBanner({
  variant,
  title,
  subtitle,
  onTap,
}: StatusBannerProps): React.ReactElement {
  const copy = DEFAULT_COPY[variant]
  const resolvedTitle = title ?? copy.title
  const resolvedSubtitle = subtitle ?? copy.subtitle
  // `closed` → MinusCircle (danger); all others → WarningTriangle (warning/advisory)
  const Icon = variant === 'closed' ? IconMinusCircle : IconWarningTriangle
  return (
    <button
      type="button"
      className={`${styles.root} ${variantClass(variant)}`}
      data-testid="station-card-status-banner"
      data-variant={variant}
      onClick={onTap}
    >
      <span className={styles.row}>
        <Icon className={styles.icon} />
        <span className={styles.textColumn}>
          <span className={styles.title}>{resolvedTitle}</span>
          <span className={styles.subtitle}>{resolvedSubtitle}</span>
        </span>
        <IconChevronRight
          className={styles.chevron}
          data-testid="station-card-status-banner-chevron"
        />
      </span>
    </button>
  )
}
